import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, BarChart2, ChevronRight, CircleHelp } from 'lucide-react';
import { Button } from '@/shared/components/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/shadcn/popover';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useCampaignDetail } from '@/entities/campaign';
import { StepProgressLoader } from '@/features/campaign-planning/components/step-progress-loader';
import { sunscreenCampaignAnalysis } from '../data/sunscreen-campaign-analysis';
import { AnalysisResults } from '../components/analysis-results';

type AnalysisState = 'metric' | 'analyzing' | 'results';

const METRIC_OPTIONS = [
  { value: 'Revenue Score', label: 'Revenue Score' },
  { value: 'View Score', label: 'View Score (Reach)' },
  { value: 'Engagement Score', label: 'Engagement Score' },
];

const ANALYZING_MSGS_KO = [
  '영상 콘텐츠 분석',
  '성과 변인 측정',
  '최적화 지표와 상관관계 분석',
  '콘텐츠 인사이트 생성',
  '크리에이터 프로필 구성',
];

const ANALYZING_MSGS_EN = [
  'Analyzing video content',
  'Measuring performance variables',
  'Correlating with optimization metric',
  'Generating content insights',
  'Building creator profiles',
];

export function CampaignSingleAnalysisPage() {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const { workspaceId, campaignId } = useParams({
    from: '/workspace/$workspaceId/campaigns/$campaignId/analysis',
  });
  const isKo = workspace.language === 'ko';

  const campaignDetailQuery = useCampaignDetail(campaignId);
  const campaign = campaignDetailQuery.data ?? null;

  const [metric, setMetric] = useState('View Score');
  const [analysisState, setAnalysisState] = useState<AnalysisState>('metric');

  const analyzingMsgs = isKo ? ANALYZING_MSGS_KO : ANALYZING_MSGS_EN;

  const handleBack = () => {
    navigate({
      to: '/workspace/$workspaceId/campaigns/$campaignId/',
      params: { workspaceId, campaignId },
    });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-6 py-4">
        <Button variant="ghost" size="icon" className="size-8 shrink-0" onClick={handleBack}>
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-2">
          <BarChart2 className="size-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold leading-tight">Campaign Analysis</h1>
            {campaign && (
              <p className="text-xs text-muted-foreground">{campaign.name}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6">
        {/* Step 1: Metric Selection */}
        {analysisState === 'metric' && (
          <div className="flex flex-col gap-6 max-w-2xl">
            <div className="rounded-xl border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">
                {isKo ? '분석 기준 지표를 선택하세요' : 'Select an optimization metric'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {isKo
                  ? '선택한 지표를 기준으로 상위 성과 포스트를 분석해 콘텐츠 전략을 도출합니다.'
                  : "We'll analyze top-performing posts by the selected metric to derive your content strategy."}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {isKo ? '최적화 지표' : 'Optimization Metric'}
              </label>
              <div className="flex items-center gap-2">
                <Select value={metric} onValueChange={setMetric}>
                  <SelectTrigger className="max-w-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METRIC_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {metric === 'View Score' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <CircleHelp className="size-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="start" side="right">
                      <div className="flex flex-col gap-3">
                        <p className="text-sm font-semibold text-foreground">Key Performance Metric</p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Comparing raw view counts between a creator with 1M followers and one with 10K followers always favors the larger account. To correct for this, we normalized both absolute views and the views-per-follower ratio separately, then summed them into a single View Score. This allows comparison of reach efficiency based on content quality, independent of account size.
                        </p>
                        <p className="rounded-md bg-muted px-3 py-2 font-mono text-xs text-foreground">
                          View Score = MinMaxScaler(Views) + MinMaxScaler(Views / Followers)
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            <Button
              onClick={() => setAnalysisState('analyzing')}
              className="gap-2 self-start"
              size="lg"
            >
              {isKo ? '분석 시작' : 'Start Analysis'}
              <ChevronRight className="size-4" />
            </Button>
          </div>
        )}

        {/* Step 2: Analyzing */}
        {analysisState === 'analyzing' && (
          <div className="flex flex-col gap-6 max-w-2xl">
            <div className="rounded-xl border bg-muted/30 p-4">
              <p className="text-sm font-medium text-foreground">
                {isKo ? '캠페인 포스트 데이터를 분석하고 있어요' : 'Analyzing campaign post data'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {campaign?.name} · {metric}
              </p>
            </div>
            <StepProgressLoader
              messages={analyzingMsgs}
              intervalMs={900}
              onComplete={() => setAnalysisState('results')}
            />
          </div>
        )}

        {/* Step 3: Results */}
        {analysisState === 'results' && (
          <div className="flex flex-col gap-4">
            <button
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors self-start"
              onClick={() => setAnalysisState('metric')}
            >
              <ArrowLeft className="size-3.5" />
              {isKo ? '지표 재선택' : 'Change metric'}
            </button>
            <AnalysisResults
              report={sunscreenCampaignAnalysis}
              campaignName={campaign?.name}
            />
          </div>
        )}
      </div>
    </div>
  );
}
